import { createRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Title } from 'src/viewLayer/components/DesignSystem';
import { t } from 'src/viewLayer/helpers/translations';
import { Privacy } from 'src/viewLayer/components/shared/Privacy';
import { inputConfig } from './config';
import useCurrentInputState from 'src/viewLayer/components/hooks/useCurrentInputState';
import { EmailSignup } from 'src/viewLayer/components/nodes/InputNode/EmailSignup';
import { InputNodeProps, SubmitParamsType, InputNamesType } from './types';
import { useClearPasswordField } from 'src/viewLayer/components/hooks/useClearPasswordField';

import { InputNodeContainer, InputContainer } from './index.styled';

/*
  There are 2 states for user input:
    1. currentInput = each input node's local state
    2. userInput = validated flow-level user state

  1. currentInput contains unvalidated (by the backend) input from the user and error messages related to invalid
     input. Each input node can have multiple or single types of currentUserInput, eg. email & password or simply email
  2. once the input has been submitted & validated to the backend we store it as flow level userInput state
  */

export default ({
  nodeConfig,
  goToNextNode,
  userInput,
  setUserInput,
  emailVerificationTarget,
  setUserData,
  trackingInfo
}: InputNodeProps): JSX.Element => {
  import(`src/viewLayer/components/nodes/InputNode/styling/${ nodeConfig.inputTheme || 'index' }.scss`);
  const nodeInputs = nodeConfig.partials;
  // Store all available inputs in the current state.
  // The InputNode is the parent to the different inputs & could display all inputs at once.
  // If we don't use all available inputs, the fields are populated wrongly on nextNode render.
  // Also moving back & forth using the browser buttons breaks otherwise.
  const showSingleInput = nodeInputs.length === 1;
  const currentInput = useCurrentInputState(Object.keys(inputConfig) as InputNamesType[], userInput, showSingleInput);
  const [inProgress, setInProgress] = useState(false);
  const { learnLang, flowId } = useParams();
  const scrollToRef = createRef<HTMLDivElement>();

  useClearPasswordField(nodeInputs, currentInput);

  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation_token');

  const submitParams: SubmitParamsType = {
    setInProgress,
    currentInput,
    userInput,
    setUserInput,
    nodeInputs,
    goToNextNode,
    learnLang,
    emailVerificationTarget,
    setUserData,
    trackingInfo,
    flowId,
    invitationToken,
  };

  return (
    <InputNodeContainer
      id="uat-input"
      className="InputNode"
    >
      <InputContainer alignItems="center">
        <Title size={{ xxxsmall: 'small', medium: 'default' }} textAlign="center">{t(nodeConfig.title)}</Title>
        <EmailSignup
          nodeInputs={ nodeInputs }
          submitParams={ submitParams }
          inProgress={ inProgress }
          nodeConfig={ nodeConfig }
        />
        {nodeConfig?.options?.showPrivacy && <Privacy scrollToRef={scrollToRef} />}
      </InputContainer>
    </InputNodeContainer>
  );
};
